import React, { createContext, useCallback, useContext, useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import UpgradeModal from "../components/UpgradeModal";
import CryptoPaymentModal from "../components/CryptoPaymentModal";

const OptionsEntitlementsContext = createContext({});

const FEATURES_COPY = {
  POSITION_DETAILS: {
    title: "Detalhes avançados da posição",
    description: "Acompanhe pernas individuais, rolagens e todo o ciclo da estratégia com o GENI PRO.",
    benefits: [
      "Complete operations history",
      "Advanced performance & capital analysis",
      "Margin × Volatility Analysis",
      "Delta & Price Alerts",
      "Roll analysis & position cycles",
      "BTC Outlook & Futures Hedge",
    ]
  },
  HISTORY: {
    title: "Histórico completo",
    description: "Acesse todo o histórico de suas operações e controle sua trajetória de forma profissional.",
    benefits: [
      "Complete operations history",
      "Advanced performance & capital analysis",
      "Margin × Volatility Analysis",
      "Delta & Price Alerts",
      "Roll analysis & position cycles",
      "BTC Outlook & Futures Hedge",
    ]
  },
  ANALYTICS: {
    title: "Analytics avançado",
    description: "Aprofunde sua análise de risco e melhore sua rentabilidade com dados detalhados.",
    benefits: [
      "Complete operations history",
      "Advanced performance & capital analysis",
      "Margin × Volatility Analysis",
      "Delta & Price Alerts",
      "Roll analysis & position cycles",
      "BTC Outlook & Futures Hedge",
    ]
  },
  PERFORMANCE: {
    title: "Performance avançada",
    description: "Monitore sua evolução financeira com métricas estendidas, projeções de patrimônio e análise de capital.",
    benefits: [
      "Complete operations history",
      "Advanced performance & capital analysis",
      "Margin × Volatility Analysis",
      "Delta & Price Alerts",
      "Roll analysis & position cycles",
      "BTC Outlook & Futures Hedge",
    ]
  },
  ALERTS: {
    title: "Alertas ilimitados",
    description: "Monitore quantas posições precisar em tempo real e não perca nenhuma oportunidade de ajuste.",
    benefits: [
      "Complete operations history",
      "Advanced performance & capital analysis",
      "Margin × Volatility Analysis",
      "Delta & Price Alerts",
      "Roll analysis & position cycles",
      "BTC Outlook & Futures Hedge",
    ]
  },
  BTC_OUTLOOK: {
    title: "BTC Outlook PRO",
    description: "Acompanhe o viés de mercado do Bitcoin para 30–60 dias sintetizado pelo nosso time.",
    benefits: [
      "Complete operations history",
      "Advanced performance & capital analysis",
      "Margin × Volatility Analysis",
      "Delta & Price Alerts",
      "Roll analysis & position cycles",
      "BTC Outlook & Futures Hedge",
    ]
  },
  GEX: {
    title: "Gamma Exposure",
    description: "Acesse o regime gamma, Gamma Flip e os principais picos positivos e negativos do BTC.",
    benefits: ["Gamma Regime", "Gamma Flip", "Positive Peak (P1)", "Negative Peak (N1)"]
  },
  MARGIN_VOLATILITY: {
    title: "Margin × Volatility",
    description: "Relacione margem, volatilidade implícita e estresse das gregas da sua carteira.",
    benefits: ["Utilização de margem", "IV Rank", "Greek Stress", "Conservative Level"]
  },
  VOLATILITY_FORECAST: {
    title: "Previsão de volatilidade",
    description: "Compare o DVOL atual com a previsão HAR+DVOL de 30 dias e o spread de volatilidade.",
    benefits: ["Volatilidade Prevista 30D", "Spread de Volatilidade", "Histórico de previsões"]
  },
  HISTORY_CYCLES: {
    title: "Ciclos e rolagens",
    description: "Acompanhe o ciclo completo de cada posição e suas rolagens no GENI Options PRO.",
    benefits: ["Ciclos de posição", "Rolagens", "Linha do tempo da estratégia"]
  }
};

export const OptionsEntitlementsProvider = ({ children }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCryptoModal, setShowCryptoModal] = useState(false);
  const [cryptoInitialPlan, setCryptoInitialPlan] = useState(null);
  const [upgradeFeature, setUpgradeFeature] = useState("POSITION_DETAILS");

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }
    try {
      const response = await api.get("/options/entitlements");
      setProfile(response.data);
    } catch (e) {
      console.error("Error loading entitlements profile:", e);
      // Preservar o perfil já carregado e evitar anular em caso de falhas temporárias
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const isPro = profile?.is_pro === true;
  const serverFeatures = profile?.features || {};

  const entitlements = {
    POSITION_DETAILS: true,
    HISTORY_LONG_RANGES: true,
    POSITION_CYCLES: Boolean(serverFeatures.HISTORY_CYCLES),
    ROLLS: Boolean(serverFeatures.HISTORY_ROLLS),
    ANALYTICS_LONG_RANGES: true,
    CUSTOM_PERIOD: Boolean(serverFeatures.CUSTOM_PERIOD),
    DETAILED_ANALYTICS: Boolean(serverFeatures.DETAILED_ANALYTICS),
    PERFORMANCE_LONG_RANGES: true,
    ACCOUNT_EVOLUTION: Boolean(serverFeatures.ACCOUNT_EVOLUTION),
    EQUITY_PROJECTION: Boolean(serverFeatures.EQUITY_PROJECTION),
    CAPITAL_PERIODS: Boolean(serverFeatures.CAPITAL_PERIODS),
    MAX_ACTIVE_ALERTS: profile?.limits?.active_alerts ?? Infinity,
    BTC_OUTLOOK: Boolean(serverFeatures.BTC_OUTLOOK),
    GEX: Boolean(serverFeatures.GEX),
    MARGIN_VOLATILITY: Boolean(serverFeatures.MARGIN_VOLATILITY),
    VOLATILITY_FORECAST: Boolean(serverFeatures.VOLATILITY_FORECAST),
    VOLATILITY_HISTORY: Boolean(serverFeatures.VOLATILITY_HISTORY),
  };

  const openUpgradeModal = (featureKey) => {
    if (FEATURES_COPY[featureKey]) {
      setUpgradeFeature(featureKey);
    } else {
      setUpgradeFeature("POSITION_DETAILS");
    }
    setShowUpgradeModal(true);
  };

  const handleSelectPlan = (plan, isUpgrade, price) => {
    setCryptoInitialPlan(plan);
    setShowUpgradeModal(false);
    setShowCryptoModal(true);
  };

  const modalContent = FEATURES_COPY[upgradeFeature] ? {
    optionsBadge: "GENI Options PRO",
    title: FEATURES_COPY[upgradeFeature].title,
    description: FEATURES_COPY[upgradeFeature].description,
    benefitsTitle: "BENEFÍCIOS PRO",
    benefits: FEATURES_COPY[upgradeFeature].benefits,
    disclaimer: "Ativação global. Sem fidelidade.",
    monthlyPlanLabel: "PRO Mensal",
    yearlyPlanLabel: "PRO Anual",
    monthlyIntervalLabel: "/ mês",
    yearlyIntervalLabel: "/ ano",
    monthlyButtonLabel: "Assinar PRO Mensal",
    yearlyButtonLabel: "Assinar PRO Anual",
    otherMethodLabel: "Utilizar outro método de pagamento",
    bestValueLabel: "MELHOR VALOR",
    paymentNote: "Pagamento seguro em BRL processado via Stripe.",
    redirectingTitle: "Redirecionando para o pagamento...",
    redirectingBody: "Aguarde enquanto carregamos a página segura de pagamento da Stripe.",
  } : null;

  return (
    <OptionsEntitlementsContext.Provider
      value={{
        isPro,
        entitlements,
        profile,
        loading,
        openUpgradeModal,
        refetchProfile: fetchProfile
      }}
    >
      {children}
      
      {/* Centralized Options Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSelectPlan={handleSelectPlan}
        context="options"
        returnContext="options"
        content={modalContent}
        userEmail={user?.email}
      />
      <CryptoPaymentModal
        isOpen={showCryptoModal}
        onClose={() => setShowCryptoModal(false)}
        initialPlan={cryptoInitialPlan}
        onBack={() => {
          setShowCryptoModal(false);
          setShowUpgradeModal(true);
        }}
      />
    </OptionsEntitlementsContext.Provider>
  );
};

export const useOptionsEntitlements = () => {
  const context = useContext(OptionsEntitlementsContext);
  if (!context) {
    throw new Error("useOptionsEntitlements must be used within OptionsEntitlementsProvider");
  }
  return context;
};
